import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { User, Shield, Briefcase } from "lucide-react";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any | null;
  onSuccess: () => void;
}

const MODULE_ACCESS_OPTIONS = [
  { id: "quotations", label: "Cotizaciones" },
  { id: "work-orders", label: "Ordenes de trabajo" },
  { id: "processes", label: "Procesos" },
  { id: "quality-control", label: "Control de calidad" },
  { id: "traceability", label: "Trazabilidad" },
];

const DEFAULT_EMAIL_DOMAIN = "@magmacomposites.com";

const applyDefaultEmailDomain = (email: string) => {
  const trimmed = email.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.includes("@") ? trimmed : `${trimmed}${DEFAULT_EMAIL_DOMAIN}`;
};

const toLocalDateTimeInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export function UserFormDialog({ open, onOpenChange, user, onSuccess }: UserFormDialogProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [customModuleAccess, setCustomModuleAccess] = useState(false);
  const [allowedModules, setAllowedModules] = useState<string[]>([]);
  const [temporaryModuleAccess, setTemporaryModuleAccess] = useState(false);
  const [temporaryAllowedModules, setTemporaryAllowedModules] = useState<string[]>([]);
  const [temporaryAccessUntil, setTemporaryAccessUntil] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "operator",
    is_active: true,
    // Profile data
    phone: "",
    department: "",
    position: "",
    employee_id: "",
    tag: "",
    area: "",
    hire_date: "",
    notes: "",
    skill_level: "" as "" | "A" | "B" | "C",
  });

  useEffect(() => {
    if (user) {
      const moduleAccess = Array.isArray(user.profiles?.allowed_modules)
        ? user.profiles.allowed_modules
        : [];
      const temporaryModuleAccessList = Array.isArray(user.profiles?.temporary_allowed_modules)
        ? user.profiles.temporary_allowed_modules
        : [];
      setFormData({
        email: user.email || "",
        password: "",
        full_name: user.profiles?.full_name || user.full_name || "",
        role: user.user_roles?.[0]?.role || "operator",
        is_active: user.is_active ?? true,
        phone: user.profiles?.phone || "",
        department: user.profiles?.department || "",
        position: user.profiles?.position || "",
        employee_id: user.profiles?.employee_id || "",
        tag: user.profiles?.tag || "",
        area: user.profiles?.area || "",
        hire_date: user.profiles?.hire_date || "",
        notes: user.profiles?.notes || "",
        skill_level: user.profiles?.skill_level || "",
      });
      setAllowedModules(moduleAccess);
      setTemporaryAllowedModules(temporaryModuleAccessList);
      setTemporaryAccessUntil(toLocalDateTimeInput(user.profiles?.temporary_access_until));
      setCustomModuleAccess(Array.isArray(user.profiles?.allowed_modules));
      setTemporaryModuleAccess(temporaryModuleAccessList.length > 0);
    } else {
      setFormData({
        email: "",
        password: "",
        full_name: "",
        role: "operator",
        is_active: true,
        phone: "",
        department: "",
        position: "",
        employee_id: "",
        tag: "",
        area: "",
        hire_date: "",
        notes: "",
        skill_level: "",
      });
      setAllowedModules([]);
      setCustomModuleAccess(false);
      setTemporaryAllowedModules([]);
      setTemporaryAccessUntil("");
      setTemporaryModuleAccess(false);
    }
    setActiveTab("basic");
  }, [user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailToSave = applyDefaultEmailDomain(formData.email);

    if (!emailToSave) {
      toast.error("El email es requerido");
      return;
    }

    if (!user && !formData.password) {
      toast.error("La contrasena es requerida para nuevos usuarios");
      return;
    }

    setLoading(true);
    try {
      const hasCustomAccess = formData.role === "operator" && customModuleAccess;
      const profileModuleAccess = hasCustomAccess ? allowedModules : null;
      const hasTemporaryAccess = hasCustomAccess && temporaryModuleAccess;
      let temporaryModulesToSave: string[] | null = null;
      let temporaryAccessUntilToSave: string | null = null;

      if (hasTemporaryAccess) {
        if (!temporaryAccessUntil) {
          toast.error("Define la fecha limite del acceso temporal");
          return;
        }
        if (temporaryAllowedModules.length === 0) {
          toast.error("Selecciona al menos un modulo temporal");
          return;
        }
        const expiresAt = new Date(temporaryAccessUntil);
        if (Number.isNaN(expiresAt.getTime()) || expiresAt <= new Date()) {
          toast.error("La fecha de acceso temporal debe ser futura");
          return;
        }
        temporaryModulesToSave = temporaryAllowedModules;
        temporaryAccessUntilToSave = expiresAt.toISOString();
      }

      if (user) {
        // Update profile (main user data table)
        const { data: updatedProfile, error: profileError } = await api
          .from("profiles")
          .update({
            full_name: formData.full_name,
            is_active: formData.is_active,
            phone: formData.phone || null,
            department: formData.department || null,
            position: formData.position || null,
            employee_id: formData.employee_id || null,
            tag: formData.tag || null,
            area: formData.area || null,
            hire_date: formData.hire_date || null,
            notes: formData.notes || null,
            skill_level: formData.skill_level || null,
            allowed_modules: profileModuleAccess,
            temporary_allowed_modules: temporaryModulesToSave,
            temporary_access_until: temporaryAccessUntilToSave,
          })
          .eq("id", user.id)
          .select();

        console.log("[UserFormDialog] Profile update result:", { updatedProfile, profileError });

        if (profileError) throw profileError;

        if (!updatedProfile || updatedProfile.length === 0) {
          throw new Error("No se pudo actualizar el perfil - posible restricción de permisos");
        }

        // Update role - delete existing and insert new
        await api
          .from("user_roles")
          .delete()
          .eq("user_id", user.id);

        const { error: roleError } = await api
          .from("user_roles")
          .insert({ user_id: user.id, role: formData.role });

        if (roleError) {
          console.error("[UserFormDialog] Role insert error:", roleError);
          // Don't throw - role update is secondary
        }

        toast.success("Usuario actualizado correctamente");
      } else {
        // Create new user
        const { data: newUser, error: userError } = await api
          .from("users")
          .insert({
            email: emailToSave,
            password_hash: formData.password, // In real app, hash this
            full_name: formData.full_name,
            is_active: formData.is_active,
          })
          .select()
          .single();

        if (userError) throw userError;

        // Create profile
        const { error: profileError } = await api.from("profiles").insert({
          id: newUser.id,
          full_name: formData.full_name,
          phone: formData.phone || null,
          department: formData.department || null,
          position: formData.position || null,
          employee_id: formData.employee_id || null,
          tag: formData.tag || null,
          area: formData.area || null,
          hire_date: formData.hire_date || null,
          notes: formData.notes || null,
          skill_level: formData.skill_level || null,
          allowed_modules: profileModuleAccess,
          temporary_allowed_modules: temporaryModulesToSave,
          temporary_access_until: temporaryAccessUntilToSave,
        });

        if (profileError) throw profileError;

        // Create role
        const { error: roleError } = await api.from("user_roles").insert({
          user_id: newUser.id,
          role: formData.role,
        });

        if (roleError) throw roleError;

        toast.success("Usuario creado correctamente");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Error al guardar usuario");
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId: string, checked: boolean) => {
    setAllowedModules((prev) =>
      checked ? Array.from(new Set([...prev, moduleId])) : prev.filter((id) => id !== moduleId)
    );
  };

  const toggleTemporaryModule = (moduleId: string, checked: boolean) => {
    setTemporaryAllowedModules((prev) =>
      checked ? Array.from(new Set([...prev, moduleId])) : prev.filter((id) => id !== moduleId)
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <User className="w-5 h-5" />
            {user ? "Editar Usuario" : "Nuevo Usuario"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 h-auto">
              <TabsTrigger value="basic" className="text-xs sm:text-sm py-2">Básico</TabsTrigger>
              <TabsTrigger value="profile" className="text-xs sm:text-sm py-2">Perfil</TabsTrigger>
              <TabsTrigger value="role" className="text-xs sm:text-sm py-2">Rol</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  onBlur={() => {
                    if (user) {
                      return;
                    }
                    setFormData((prev) => {
                      const normalized = applyDefaultEmailDomain(prev.email);
                      return normalized && normalized !== prev.email
                        ? { ...prev, email: normalized }
                        : prev;
                    });
                  }}
                  placeholder="nombre.apellido@magmacomposites.com"
                  disabled={!!user}
                />
              </div>

              <div className="space-y-2">
                <Label>{user ? "Nueva Contrasena (dejar vacio para no cambiar)" : "Contrasena *"}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="********"
                />
              </div>

              <div className="space-y-2">
                <Label>Nombre Completo</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Juan Garcia"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Usuario Activo</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </TabsContent>

            <TabsContent value="profile" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+34 600 000 000"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label>ID Empleado</Label>
                  <Input
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="EMP-001"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Firma/Tag de operario</Label>
                <Input
                  value={formData.tag}
                  onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                  placeholder="jperez"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Departamento</Label>
                  <Select
                    value={formData.department}
                    onValueChange={(value) => setFormData({ ...formData, department: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="produccion">Producción</SelectItem>
                      <SelectItem value="calidad">Calidad</SelectItem>
                      <SelectItem value="almacen">Almacén</SelectItem>
                      <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                      <SelectItem value="administracion">Administración</SelectItem>
                      <SelectItem value="direccion">Dirección</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Puesto</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Operador de línea"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nivel de Habilidad</Label>
                  <Select
                    value={formData.skill_level || "__none__"}
                    onValueChange={(value) => setFormData({ ...formData, skill_level: value === "__none__" ? "" : value as "A" | "B" | "C" })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      <SelectItem value="A">A - Experto (Alta complejidad)</SelectItem>
                      <SelectItem value="B">B - Intermedio (Tareas estándar)</SelectItem>
                      <SelectItem value="C">C - Básico (Tareas simples)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Área</Label>
                  <Input
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    placeholder="Línea 1"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Alta</Label>
                  <Input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                />
              </div>
            </TabsContent>

            <TabsContent value="role" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Rol del Usuario *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar rol..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="operator">Operador</SelectItem>
                    <SelectItem value="quality">Calidad</SelectItem>
                    <SelectItem value="warehouse">Almacen</SelectItem>
                    <SelectItem value="maintenance">Mantenimiento</SelectItem>
                    <SelectItem value="viewer">Visor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 rounded-lg border bg-muted/30">
                <h4 className="font-medium mb-2">Descripcion del Rol</h4>
                <p className="text-sm text-muted-foreground">
                  {formData.role === "admin" && "Acceso total al sistema. Puede gestionar usuarios, configuracion y todos los modulos."}
                  {formData.role === "supervisor" && "Acceso a la mayoria de funciones. Puede gestionar produccion, calidad e inventario."}
                  {formData.role === "operator" && "Acceso basico. Puede ver ordenes de trabajo e iniciar procesos de produccion."}
                  {formData.role === "quality" && "Especializado en calidad. Puede crear y aprobar inspecciones de calidad."}
                  {formData.role === "warehouse" && "Especializado en almacen. Puede gestionar inventario y trazabilidad."}
                  {formData.role === "maintenance" && "Especializado en mantenimiento. Puede gestionar maquinas y moldes."}
                  {formData.role === "viewer" && "Solo lectura. Puede visualizar informacion pero no modificar nada."}
                </p>
              </div>

              {formData.role === "operator" && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Acceso personalizado por modulo</Label>
                    <Switch
                      checked={customModuleAccess}
                      onCheckedChange={(checked) => {
                        setCustomModuleAccess(checked);
                        if (!checked) {
                          setAllowedModules([]);
                          setTemporaryModuleAccess(false);
                          setTemporaryAllowedModules([]);
                          setTemporaryAccessUntil("");
                        }
                      }}
                    />
                  </div>

                  {customModuleAccess && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {MODULE_ACCESS_OPTIONS.map((module) => (
                          <label key={module.id} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={allowedModules.includes(module.id)}
                              onCheckedChange={(checked) => toggleModule(module.id, Boolean(checked))}
                            />
                            <span>{module.label}</span>
                          </label>
                        ))}
                      </div>

                      <div className="border-t pt-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Acceso temporal a otros modulos</Label>
                          <Switch
                            checked={temporaryModuleAccess}
                            onCheckedChange={(checked) => {
                              setTemporaryModuleAccess(checked);
                              if (!checked) {
                                setTemporaryAllowedModules([]);
                                setTemporaryAccessUntil("");
                              }
                            }}
                          />
                        </div>

                        {temporaryModuleAccess && (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {MODULE_ACCESS_OPTIONS.map((module) => (
                                <label key={module.id} className="flex items-center gap-2 text-sm">
                                  <Checkbox
                                    checked={temporaryAllowedModules.includes(module.id)}
                                    onCheckedChange={(checked) => toggleTemporaryModule(module.id, Boolean(checked))}
                                  />
                                  <span>{module.label}</span>
                                </label>
                              ))}
                            </div>

                            <div className="space-y-2">
                              <Label>Acceso temporal hasta</Label>
                              <Input
                                type="datetime-local"
                                value={temporaryAccessUntil}
                                onChange={(e) => setTemporaryAccessUntil(e.target.value)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Si activas el acceso personalizado, solo podra entrar a los modulos marcados.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? "Guardando..." : user ? "Actualizar" : "Crear Usuario"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
